/**
 * Utilidades para conversión y manejo de colores
 */

/**
 * Convierte un color hexadecimal a RGBA string
 * @param {string} hex - Color en formato hex (#RRGGBB o #RGB)
 * @param {number} alpha - Valor de transparencia (0-1)
 * @returns {string} Color en formato rgba(r, g, b, alpha)
 */
export const hexToRgba = (hex, alpha = 1) => {
  // Validar que hex no sea null o undefined
  if (!hex) {
    console.warn('hexToRgba: hex is null or undefined, using default black');
    hex = '#000000';
  }

  // Eliminar el # si está presente
  hex = hex.replace('#', '');

  // Expandir formato corto (#RGB a #RRGGBB)
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Convierte un color hexadecimal string a número hexadecimal para PixiJS
 * @param {string} hex - Color en formato hex (#RRGGBB)
 * @returns {number} Número hexadecimal (0xRRGGBB)
 */
export const hexToPixiNumber = (hex) => {
  // Validar que hex no sea null o undefined
  if (!hex) {
    console.warn('hexToPixiNumber: hex is null or undefined, using default black');
    hex = '#000000';
  }

  hex = hex.replace('#', '');
  return parseInt(hex, 16);
};

/**
 * Deriva todos los formatos de color necesarios desde un hex
 * @param {string} hexColor - Color base en formato hex
 * @returns {object} Objeto con todos los formatos: {main, light, border, hex}
 */
export const deriveTeamColors = (hexColor) => {
  return {
    main: hexColor,
    light: hexToRgba(hexColor, 0.1),
    border: hexToRgba(hexColor, 0.3),
    hex: hexToPixiNumber(hexColor),
  };
};

/**
 * Valida que dos colores sean diferentes
 * @param {string} color1 - Primer color en hex
 * @param {string} color2 - Segundo color en hex
 * @returns {boolean} True si los colores son diferentes
 */
export const validateTeamColors = (color1, color2) => {
  if (!color1 || !color2) return false;
  return color1.toLowerCase() !== color2.toLowerCase();
};

/**
 * Calcula el brillo relativo de un color (0-255)
 * Usa la fórmula de luminosidad percibida
 * @param {string} hex - Color en formato hex
 * @returns {number} Brillo entre 0-255
 */
export const getColorBrightness = (hex) => {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Fórmula de luminosidad percibida
  return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * Determina si un color es claro u oscuro
 * @param {string} hex - Color en formato hex
 * @returns {boolean} True si el color es claro
 */
export const isLightColor = (hex) => {
  return getColorBrightness(hex) > 128;
};
